import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { applyCouponApi } from '@/services/api'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import Alert from '@/components/ui/Alert'
import Badge from '@/components/ui/Badge'
import { Tag, X, Check, TrendingDown, Calendar } from 'lucide-react'

export default function CouponApplication() {
  const queryClient = useQueryClient()
  const [couponCode, setCouponCode] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const { data: activeCoupons } = useQuery({
    queryKey: ['active-coupons'],
    queryFn: () => applyCouponApi.getActive(),
  })

  const applyMutation = useMutation({
    mutationFn: (code: string) => applyCouponApi.apply(code),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['active-coupons'] })
      setCouponCode('')
      setSuccess(response.message || 'Coupon applied successfully!')
      setError('')
      setTimeout(() => setSuccess(''), 5000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to apply coupon')
      setSuccess('')
    },
  })

  const removeMutation = useMutation({
    mutationFn: (id: number) => applyCouponApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-coupons'] })
      setSuccess('Coupon removed successfully!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to remove coupon')
    },
  })

  const handleApply = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (!couponCode.trim()) {
      setError('Please enter a coupon code')
      return
    }

    applyMutation.mutate(couponCode.toUpperCase().trim())
  }

  const handleRemove = (id: number) => {
    if (confirm('Are you sure you want to remove this coupon from your subscription?')) {
      removeMutation.mutate(id)
    }
  }

  const coupons = activeCoupons?.data?.coupons || []

  const formatDiscount = (coupon: any) => {
    if (coupon.type === 'percentage') {
      return `${parseFloat(String(coupon.discount_value))}% off`
    }
    return `$${parseFloat(String(coupon.discount_value)).toFixed(2)} off`
  }

  const formatCycles = (coupon: any) => {
    if (coupon.cycles_remaining === null) {
      return 'Unlimited billing cycles'
    }
    return `${coupon.cycles_remaining} billing ${coupon.cycles_remaining === 1 ? 'cycle' : 'cycles'} remaining`
  }

  return (
    <Card title="Promotional Coupons">
      <div className="space-y-6">
        {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
        {error && <Alert type="error" message={error} onClose={() => setError('')} />}

        {/* Apply Coupon Form */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Apply a Coupon Code</h3>
          <form onSubmit={handleApply} className="flex space-x-3">
            <input
              type="text"
              className="input flex-1 uppercase"
              placeholder="Enter coupon code"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              disabled={applyMutation.isPending}
            />
            <Button
              type="submit"
              isLoading={applyMutation.isPending}
              disabled={!couponCode.trim()}
            >
              <Check className="w-4 h-4 mr-2" />
              Apply
            </Button>
          </form>
          <p className="text-xs text-gray-500 mt-2">
            Enter a promotional code to receive a discount on your subscription
          </p>
        </div>

        {/* Active Coupons */}
        {coupons.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-900">Active Coupons</h3>
            {coupons.map((coupon: any) => (
              <div
                key={coupon.id}
                className="border border-green-200 bg-green-50 rounded-lg p-4 flex items-start justify-between"
              >
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className="text-lg font-bold font-mono text-green-900">
                      {coupon.code}
                    </span>
                    <Badge status="active">Active</Badge>
                  </div>

                  <div className="space-y-1 text-sm text-gray-700">
                    <div className="flex items-center space-x-2">
                      <TrendingDown className="w-4 h-4 text-green-600" />
                      <span className="font-medium">{formatDiscount(coupon)}</span>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-green-600" />
                      <span>{formatCycles(coupon)}</span>
                    </div>

                    <p className="text-xs text-gray-600 mt-2">
                      Applied on {new Date(coupon.first_applied_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => handleRemove(coupon.id)}
                  className="text-gray-400 hover:text-red-600 transition-colors ml-4"
                  title="Remove coupon"
                  disabled={removeMutation.isPending}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ))}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-900">
                <strong>Note:</strong> Coupons will be automatically applied to your next billing cycle.
                The discount will continue for the specified number of billing cycles.
              </p>
            </div>
          </div>
        )}

        {coupons.length === 0 && (
          <div className="text-center py-6 bg-gray-50 rounded-lg">
            <Tag className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No active coupons</p>
            <p className="text-xs text-gray-400 mt-1">
              Apply a promotional code to get discounts on your subscription
            </p>
          </div>
        )}
      </div>
    </Card>
  )
}
